import { MD5 } from "../security/md5";
import { ServiceProvider } from "../provider/service-provider";
import { DialogResultVm } from "../../checkresultdialog/dialogresultvm";
import { ErrorHandler } from "../error-handler";
import { UrlHelper } from "../html/url-helper";
import { InvalidParameterError } from "../exception/invalid-parameter-error";
import { CheckResultData } from "../../checkresultdialog/checkresultdata";
import { ControlType } from "../../devexpress/controltype";
import { LocalStorageManager } from "../../core/storage/local-storage-manager";
import { MessageHelper } from "../../helper/message-helper";
import { DateHelper } from "../../helper/date-helper";

interface IStateStoringResult {
	enabled?: boolean;
	ignoreColumnOptionNames: any[];
	storageKey?: string;
	type?: "custom" | "localStorage" | "sessionStorage";
	customLoad?: (() => Promise<any> | JQueryPromise<any>);
	customSave?: ((gridState: any) => any);
	savingTimeout?: number;
}

export class StateStoring {
	public static oldHashes: any = [];
	public static registeredStateStoringChanges: KnockoutObservableArray<{ stateStoring: IStateStoringResult, hasSavedData: KnockoutObservable<boolean> }> = ko.observableArray([]);

	private static readonly settingsPrefix = "HTML:";
	private static readonly stateStoringByName: { [name: string]: Promise<any> } = {};
	public static reloadState = (id: string) => {
		try {
			jQuery("#" + id).dxDataGrid("instance").getDataSource().reload();
			return true;
		} catch (err) {
			return false;
		}
	}

	public static resetState = (id: string) => {
		try {
			jQuery("#" + id).dxDataGrid("instance").state({});
			return true;
		} catch (err) {
			return false;
		}
	}
	public static hasChanges(moduleId: number, name: string, data: any, useTimestamp: boolean) {
		let identifier = moduleId + name;
		if (useTimestamp) {
			if (data.timestamp > 0 && this.oldHashes[identifier] !== data.timestamp) {
				this.oldHashes[identifier] = data.timestamp;
				return true;
			}
			return false;
		} else {
			let newHash = MD5.computeHash(JSON.stringify(data));
			if (this.oldHashes[identifier] !== newHash) {
				this.oldHashes[identifier] = newHash;
				return true;
			}
			return false;
		}
	}

	public static checkParams(url: string, moduleId: number) {
		if (url == null) {
			throw new InvalidParameterError("url", InvalidParameterError.nullText);
		}

		if (!(moduleId > 0)) {
			throw new InvalidParameterError("moduleId", "Module id has to be greater zero");
		}
	}

	/**
	 * Create state storing options:
	 * moduleId e.g. 10 for starter
	 * name e.g. CustomerResponsibilitiesGrid
	 * useTimestamp should be true
	 */
	public static createStateStoring = (moduleId: number, name: (() => string) | string, useTimestamp: boolean = true, controlType: ControlType = ControlType.DataGrid): IStateStoringResult => {
		let hasSavedData = ko.observable(false);
		const url = UrlHelper.getStateStoringUrl();
		let curName = typeof name === "function" ? name() : name;
		curName = `${controlType}:${curName}`;
		StateStoring.checkParams(url, moduleId);
		let result: IStateStoringResult = {
			enabled: true,
			storageKey: curName,
			ignoreColumnOptionNames: [] as any[],
			type: "custom",
			customLoad: async () => {
				let savedData = await StateStoring.loadAsync(url, moduleId, curName);
				if (UrlHelper.isDebuggingEnabled()) {
					if (savedData && Object.keys(savedData).length > 0) {
						hasSavedData(true);
					} else {
						hasSavedData(false);
					}
				}

				return savedData;
			},
			customSave: (data: any) => {
				$.extend(true, data, {
					searchText: null,
					selectedRowKeys: null,
					pageIndex: 0,
				});
				if (data.columns) {
					for (let col of data.columns) {
						col.filterValues = null;
					}
				}
				if (StateStoring.hasChanges(moduleId, curName, data, useTimestamp)) {
					delete data.timestamp;
					StateStoring.saveAsync(url, moduleId, curName, data);
				}
			},
			savingTimeout: 0,
		};
		if (UrlHelper.isDebuggingEnabled()) {
			StateStoring.registeredStateStoringChanges.push({
				stateStoring: result,
				hasSavedData
			});
		}

		return result;
	}
	public static createStateStoringComputed = (moduleId: number, nameFn: KnockoutComputed<string>, useTimestamp: boolean = true, controlType: ControlType = ControlType.DataGrid): IStateStoringResult => {
		const url = UrlHelper.getStateStoringUrl();
		let name = () => `${controlType}:${nameFn()}`;
		StateStoring.checkParams(url, moduleId);
		return {
			enabled: true,
			storageKey: name as any,
			ignoreColumnOptionNames: [] as any[],
			type: "custom",
			customLoad: async () => {
				return StateStoring.loadAsync(url, moduleId, name());
			},
			customSave: (data: any) => {
				$.extend(true, data, {
					searchText: null,
					selectedRowKeys: null,
					pageIndex: 0,
				});
				if (StateStoring.hasChanges(moduleId, name(), data, useTimestamp)) {
					delete data.timestamp;
					StateStoring.saveAsync(url, moduleId, name(), data);
				}
			}
		};
	}
	public static createStateStoringPureComputed = (nameFn: () => string, useTimestamp: boolean = true, controlType: ControlType = ControlType.DataGrid): IStateStoringResult => {
		const url = UrlHelper.getStateStoringUrl();
		const moduleId = UrlHelper.getModuleId();
		const name = ko.pureComputed(() => {
			let curName = nameFn();
			return curName ? `${controlType}:${curName}` : null;
		});
		StateStoring.checkParams(url, moduleId);
		return {
			enabled: true,
			storageKey: name as any,
			ignoreColumnOptionNames: [] as any[],
			type: "custom",
			customLoad: async () => {
				return StateStoring.loadAsync(url, moduleId, name());
			},
			customSave: (data: any) => {
				$.extend(true, data, {
					searchText: null,
					selectedRowKeys: null,
					pageIndex: 0,
				});
				if (StateStoring.hasChanges(moduleId, name(), data, useTimestamp)) {
					delete data.timestamp;
					StateStoring.saveAsync(url, moduleId, name(), data);
				}
			}
		};
	}

	public static async loadAsync(url: string, moduleId: number, name: string): Promise<any> {
		// NOTE(jpg): Wenn kein Name angegeben ist, wird kein Request abgeschickt.
		// Damit läst sich das StateStoring bequem mit leeren Models aufrufen.
		if (!name) {
			return {};
		}

		// NOTE(jpg): suppress successive load requests
		let previousStateStoring = StateStoring.stateStoringByName[this.getStateStoringName(moduleId, name)];
		if (previousStateStoring) {
			return await previousStateStoring;
		}

		let loadInternalAsync = async () => {
			let result: CheckResultData = new CheckResultData();

			if (!StateStoring.hasLocalStateStorage("stateStorage") || StateStoring.hasLocalStateStorage("stateStorage")
				&& StateStoring.isLocalStateStorageTimeExpired(StateStoring.getLocalStateStorageTime("stateStorage", name))) {
				let resultData = await StateStoring.getUserSettingsAsync()
					.catch(ErrorHandler.log())
					.then(res => StateStoring.setLocalStateStorage(res))
					.then(local => StateStoring.getLocalStateStorageDataSource("stateStorage", StateStoring.settingsPrefix + name, moduleId));

				if (resultData) {
					result.resultData = resultData;
					result.success = true;
				}
			} else {
				let resultData = StateStoring.getLocalStateStorageDataSource("stateStorage", StateStoring.settingsPrefix + name, moduleId);

				if (resultData) {
					result.resultData = resultData;
					result.success = true;
				}
			}

			if (!result) {
				return undefined;
			}

			if (!result.success) {
				DialogResultVm.showDialog(result);
				return undefined;
			}

			if (result.resultData) {
				//let jsonResult = {};
				//jsonResult = $.parseJSON(result.resultData);
				this.oldHashes[moduleId + name] = MD5.computeHash(JSON.stringify(result.resultData));
				//return jsonResult;
				return result.resultData;
			}

			return undefined;
		};

		let promise = StateStoring.stateStoringByName[this.getStateStoringName(moduleId, name)] = loadInternalAsync();
		return await promise;
	}

	public static async saveAsync(url: string, moduleId: number, name: string, value: object, serialize: boolean = true, successMessage = "Layout erfolgreich gespeichert.") {
		if (!name) {
			return;
		}

		let newValue = serialize ? JSON.stringify(value) : value;

		let params = {
			moduleId,
			name: StateStoring.settingsPrefix + name,
			value: newValue
		};

		StateStoring.stateStoringByName[this.getStateStoringName(moduleId, name)] = null;
		let result: CheckResultData = await ServiceProvider.ajax.postJsonAsync({ url, params })
			.catch(ErrorHandler.log());

		setTimeout(async () => {
			await StateStoring.getUserSettingsAsync()
				.catch(ErrorHandler.log())
				.then(res => StateStoring.setLocalStateStorage(res));
		}, 1000);

		if (result && result.success) {
			MessageHelper.uiSuccess(successMessage);
		}

		if (!result || !result.success) {
			DialogResultVm.showDialog(result);
		}
	}

	public static removeAsync(url: string, moduleId: number, name: string, successMessage: string = "Layout erfolgreich gelöscht") {
		StateStoring.saveAsync(url, moduleId, name, null, false, successMessage);
	}

	public static setLocalStateStorage(data: any) {
		if (!data || !data.resultData) { return; }

		let value = {
			time: DateHelper.nowAsDateWithTime().getTime(),
			items: [] as any[],
		};

		const resultData: any[] = data.resultData;

		for (let item of resultData) {
			if (item && item.name && item.columns) {
				let column = {
					name: item.name,
					columns: item.columns,
					moduleId: item.moduleId
				}
				value.items.push(column);
			}
		}

		LocalStorageManager.getInstance().setItemToJson("stateStorage", value);
	}

	public static getLocalStateStorage(storageKey: string) {
		if (StateStoring.hasLocalStateStorage(storageKey)) {
			return LocalStorageManager.getInstance().getItemFromJson(storageKey);
		}
		return null;
	}

	public static getLocalStateStorageDataSource(localStateStorageName: string, name: string, moduleId: number): any {
		const storageKey: string = localStateStorageName;

		if (StateStoring.hasLocalStateStorage(storageKey)) {
			let result: any = LocalStorageManager.getInstance().getItemFromJson(storageKey);

			if (result && result.items) {
				let items: any[] = result.items;
				let resultItem = items.find((item) => (item.moduleId === moduleId || item.moduleId === undefined) && item.name === name);

				if (resultItem) {
					return JSON.parse(resultItem.columns);
				}

				return null;
			}
		}
		return null;
	}

	public static async getUserSettingsAsync() {
		let url = UrlHelper.getUserSettings();
		let result: CheckResultData = await ServiceProvider.ajax.getJsonNoCacheAsync({ url }).catch(ErrorHandler.log());

		if (!result) {
			return undefined;
		}

		if (!result.success) {
			DialogResultVm.showDialog(result);
			return undefined;
		}

		if (result.resultData) {
			return result;
		}

		return undefined;
	}

	public static getLocalStateStorageTime(localStateStorageName: string, name: string): number {
		let storageKey: string = localStateStorageName;
		if (StateStoring.hasLocalStateStorage(storageKey)) {
			const result: any = LocalStorageManager.getInstance().getItemFromJson(storageKey);
			if (result && result.time) {
				return result.time;
			}
		}
		return null;
	}

	public static isLocalStateStorageTimeExpired(time: number) {
		if (!time) {
			return true;
		}

		const oldTime: number = time;
		const newTime: number = DateHelper.nowAsDateWithTime().getTime();
		const differenceTime: number = 24 * 60 * 60 * 1000;

		if (oldTime && newTime - oldTime >= differenceTime) {
			return true;
		}

		return false;
	}

	private static hasLocalStateStorage(key: string) {
		if (LocalStorageManager.getInstance().hasItem(key)) {
			return true;
		}
		return false;
	}

	private static getStateStoringName(moduleId: number, name: string): string {
		return `${moduleId ?? 0}:${name}`;
	}
}